import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useParams } from 'react-router-dom';
import { usePageTitle, useGetUnauthedSigning } from '../../logic';
import { getPageUrl } from "../../utils";
import { ErrorDialog, LoadingSignatureIframe, ESignProviders, needsSigningErrors } from '../shared';
import { HelpButton } from "../shared/Help";
import { SignatureHelp } from "../shared/SignatureHelp";
import CloseIcon from '@mui/icons-material/Close';
import { EsignDisclosureAgreement } from "../shared/EsignDisclosureAgreement";
import { UnauthedComponent } from "../shared/UnauthedComponent";
import { useAppTheme } from "../../logic/useAppTheme";


export function UnauthedSigningPage({ drawerHeight }: {
  drawerHeight: number;
}) {
  usePageTitle('Welcome!');

  const { signingHash = '' } = useParams()

  const theme = useAppTheme()
  const primaryColor = theme.palette.primary.main
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const [pinIsCorrect, setPinIsCorrect] = useState(false)

  const {
    isLoading: loading, error, data, mutate: mutateGetUnauthedSigning
  } = useGetUnauthedSigning(getPageUrl(), signingHash, {
    onSuccess(data) {
      setPinIsCorrect(true)
      setSigningInvalidStatus(false) //cleanup any previous invalid status if they are trying again
      if (data?.errorMessage === needsSigningErrors.signingCompleted) {
        setIsSigningIframeLoading(false)
        setUserCompletedSigning(true)
        setOpen(true)
      } else if (data?.errorMessage === needsSigningErrors.signingCancelled) {
        setIsSigningIframeLoading(false)
        setCanceledDialogOpen(true)
      } else if (data?.errorMessage === needsSigningErrors.invalidSigningStatus) {
        setIsSigningIframeLoading(false)
        setSigningInvalidStatus(true)
        setOpen(true)
      } else {
        setOpen(true)
        setIsSigningIframeLoading(true)
      }
      if (data?.esignDisclosureHtml) {
        setNeedsToAgree(true)
      }
    },
    useErrorBoundary: false
  })

  // const [signingUrl, setSigningUrl] = useState('');
  const [isSigningIframeLoading, setIsSigningIframeLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false)
  const [open, setOpen] = useState(false)
  const [userCompletedSigning, setUserCompletedSigning] = useState(false)
  const [signingInvalidStatus, setSigningInvalidStatus] = useState(false)
  const [showErrorLoadingSigning, setShowErrorLoadingSigning] = useState(false)
  const [needsToAgree, setNeedsToAgree] = useState(false)
  const [canceledDialogOpen, setCanceledDialogOpen] = useState(false)


  const eSignProvider = data?.ESignProvider
  const esignDisclosureHtml = data?.esignDisclosureHtml

  function onAgree() {
    setNeedsToAgree(false)
  }

  function onSigningDialogClose() {
    setOpen(false)
  }

  function onSubmit(pin: string) {
    mutateGetUnauthedSigning({ Pin: pin })
  }

  function resetComponentState() {
    setIsSigningIframeLoading(false)
    setShowErrorLoadingSigning(false)
    setUserCompletedSigning(false)
  }

  const signingUrl = data?.url
  const showSigningDialog = signingUrl || userCompletedSigning || signingInvalidStatus
  const showErrorDialog = error || showErrorLoadingSigning
  const showLoadingSignatureIframe = !userCompletedSigning && !showErrorDialog && (isSigningIframeLoading || loading)
  const startLoadingIframe = !userCompletedSigning && signingUrl
  const showCompletedMessage = userCompletedSigning
  const showDisclosureAgreement = needsToAgree && !userCompletedSigning && !isSigningIframeLoading && !loading
  const showHavingTroubleButton = signingUrl && !isSigningIframeLoading
  const showCloseButton = userCompletedSigning


  // TODO: if logged in include close X in title
  return <>
    <UnauthedComponent drawerHeight={drawerHeight} onSubmit={onSubmit} loading={loading} pinIsCorrect={pinIsCorrect} text={'Open your e-signing'} text2={'Welcome </br> to your </br> e-signing'}/>
    {showSigningDialog ?
      <Dialog
        open={open}
        fullScreen={isSmall ? true : false}
        disableEscapeKeyDown={true}
        sx={{
          height: '100%',
          padding: { xs: '8px' },
          '& .MuiDialog-paper': { borderRadius: '20px', height: '100%' }
        }}
        fullWidth={true}
        maxWidth='xl'
      >
        <DialogTitle sx={{
          'borderBottom': `1px solid ${primaryColor}`,
          display: 'flex', alignItems: 'center',
          justifyContent: 'end',
          height: '55px',
        }}>
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpen(false)
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showLoadingSignatureIframe && <LoadingSignatureIframe />}

          {showDisclosureAgreement &&
            <EsignDisclosureAgreement esignDisclosureHtml={esignDisclosureHtml!} onAgree={onAgree} />
          }

          {startLoadingIframe &&
            <ESignProviders
              isSigningIframeLoading={isSigningIframeLoading}
              setIsSigningIframeLoading={setIsSigningIframeLoading}
              eSignProvider={eSignProvider}
              signingUrl={signingUrl}
              onSigningDialogClose={onSigningDialogClose}
              setUserCompletedSigning={setUserCompletedSigning}
              setShowErrorLoadingSigning={setShowErrorLoadingSigning}
              needsToAgree={needsToAgree}
            />
          }

          {showCompletedMessage &&
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 3 }}>
              <Alert severity="success" sx={{ fontSize: 'medium' }}>
                You have completed e-signing this document!
              </Alert>
            </Box>
          }

          {signingInvalidStatus &&
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 3 }}>
              <Alert severity="warning" sx={{ fontSize: 'medium' }}>
                This signing is in an invalid state.  Please try again in 15 minutes. If this issue persists, please contact your title agent.
              </Alert>
            </Box>
          }

        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'center',
            mb: 1, mr: 1,
          }}
        >
          {showHavingTroubleButton && <Button
            onClick={() => setShowHelp(!showHelp)}
            sx={{
              display: 'flex',
              minWidth: '100px',
            }}
          >Having trouble?</Button>}

          {showCloseButton && <Button
            onClick={onSigningDialogClose}
            variant="contained"
            sx={{
              display: 'flex',
              minWidth: '100px',
            }}
          >Close</Button>}
        </DialogActions>
      </Dialog> : ''
    }


    {/* HELP DIALOG */}
    <SignatureHelp open={showHelp} setOpen={setShowHelp} isSmall={isSmall} />

    {/* ERROR DIALOG*/}
    {showErrorDialog && (
      // Using empty try again func to just close the dialog. In this case, trying
      // the same req again doesn't make sense, nor does reloading the page so it's
      // just a close button so they can double check their pin etc.
      <ErrorDialog onClose={() => resetComponentState()} onTryAgain={() => { }} tryAgainText="Close">
        <>
          <Typography component="span" sx={{ display: 'block' }}>
            There was an error processing your request:
          </Typography>
          <Typography component="span" sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
            {error?.userErrorMessage || 'Unknown error'}
          </Typography>
          <Typography component="span" sx={{ display: 'block' }}>
            Please double check your pin and try again or contact us for assistance.
          </Typography>
        </>
      </ErrorDialog>
    )}

    {/* SIGNING CANCELLED OR RESET DIALOG*/}
    <Dialog open={canceledDialogOpen} onClose={() => setCanceledDialogOpen(false)}>
      <DialogTitle>
        Signing Canceled
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          This signing has been canceled. You may close this window.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ m: 1 }}>
        <HelpButton />
        <Button
          onClick={() => setCanceledDialogOpen(false)}
          sx={{
            borderRadius: "100px", textTransform: 'none', ml: 1,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  </>
}
